<template>
  <section class="gallery-detail">
    <div class="detail-content">
      <router-link class="back-link" :to="{ name: 'marketCollections' }">
        ← NFTs
      </router-link>

      <p v-if="loading" class="state-message">Loading NFT…</p>
      <p v-else-if="error" class="state-message error-message">{{ error }}</p>

      <article v-else-if="nft" class="detail-card">
        <div class="image-panel">
          <img v-src="nft.imageUrl" :alt="nft.name" />
        </div>
        <div class="information-panel">
          <p class="eyebrow">{{ nft.collectionName }}</p>
          <h1>{{ nft.name }}</h1>
          <p class="token-id">Token #{{ nft.tokenId }}</p>

          <section class="description-section">
            <h2>Description</h2>
            <p>{{ nft.description || 'No description available.' }}</p>
          </section>

          <dl class="metadata-list">
            <div>
              <dt>Network</dt>
              <dd>{{ nft.chainName || '—' }}</dd>
            </div>
            <div>
              <dt>Token standard</dt>
              <dd>{{ nft.nftStandard || '—' }}</dd>
            </div>
            <div>
              <dt>Contract address</dt>
              <dd class="contract-address">{{ nft.contractAddress || '—' }}</dd>
            </div>
          </dl>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { getOnchainNftGalleryItem } from '@/api/nft.js';

const route = useRoute();
const nft = ref(null);
const loading = ref(false);
const error = ref('');

const loadNft = async (id) => {
  loading.value = true;
  error.value = '';
  nft.value = null;
  try {
    const { data: response } = await getOnchainNftGalleryItem(id);
    nft.value = response.result;
    if (!nft.value) error.value = 'NFT not found.';
  } catch (err) {
    error.value = err?.response?.status === 404 ? 'NFT not found.' : 'NFT details could not be loaded.';
  } finally {
    loading.value = false;
  }
};

watch(() => route.params.id, loadNft, { immediate: true });
</script>

<style scoped lang="less">
.gallery-detail {
  min-height: 60vh;
  padding: 3rem 8% 6rem;
  background: #ecf1f9;
}
.detail-content {
  max-width: 120rem;
  margin: 0 auto;
}
.back-link {
  display: inline-block;
  margin-bottom: 2rem;
  color: #566ffe;
  font-size: 1.5rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}
.state-message {
  padding: 5rem 0;
  color: #6f7d95;
  font-size: 1.6rem;
  text-align: center;
}
.error-message {
  color: #b42318;
}
.detail-card {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 4rem;
  padding: 3rem;
  border: 1px solid #e2e8f0;
  border-radius: 1.6rem;
  background: #fff;
  box-shadow: 0 2px 8px rgb(15 45 68 / 8%);
}
.image-panel {
  display: flex;
  min-height: 32rem;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 1.2rem;
  background: #f7f8fb;
  img {
    display: block;
    width: 100%;
    max-height: 65rem;
    object-fit: contain;
  }
}
.information-panel {
  color: #183b56;
  h1 {
    margin: 0.6rem 0;
    font-size: 2.8rem;
    overflow-wrap: anywhere;
  }
}
.eyebrow,
.token-id {
  margin: 0;
  color: #6f7d95;
  font-size: 1.4rem;
}
.description-section {
  margin-top: 3rem;
  h2 {
    margin: 0 0 0.8rem;
    font-size: 1.7rem;
  }
  p {
    margin: 0;
    color: #536477;
    font-size: 1.5rem;
    line-height: 1.7;
    white-space: pre-line;
    overflow-wrap: anywhere;
  }
}
.metadata-list {
  margin: 3rem 0 0;
  > div {
    display: grid;
    grid-template-columns: 13rem minmax(0, 1fr);
    gap: 1rem;
    padding: 1.2rem 0;
    border-top: 1px solid #e9edf3;
  }
  dt {
    color: #6f7d95;
    font-size: 1.35rem;
  }
  dd {
    margin: 0;
    font-size: 1.4rem;
    overflow-wrap: anywhere;
  }
  .contract-address {
    font-family: monospace;
  }
}
@media screen and (max-width: 760px) {
  .gallery-detail {
    padding: 2rem 5% 4rem;
  }
  .detail-card {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 1.5rem;
  }
  .image-panel {
    min-height: 24rem;
  }
  .information-panel h1 {
    font-size: 2.3rem;
  }
  .metadata-list > div {
    grid-template-columns: 10rem minmax(0, 1fr);
  }
}
</style>
